import { useEffect, useMemo, useState, FormEvent } from 'react';
import {
  Plus,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Calendar,
  Coins,
  CreditCard,
  Pencil,
  Trash2,
  Wallet,
} from 'lucide-react';
import api from '../services/api';
import { PageLoading } from '../components/common/Loading';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SummaryCard from '../components/dashboard/SummaryCard';

interface Debt {
  id: number;
  name: string;
  original_balance: number;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  due_day?: number;
  notes?: string;
  created_at: string;
}

interface PayoffProjection {
  debt_id: number;
  name: string;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  months_to_payoff: number;
  total_interest: number;
  payoff_date: string;
}

interface PayoffPlan {
  strategy: 'avalanche' | 'snowball';
  extra_payment: number;
  total_months: number;
  total_interest: number;
  total_paid: number;
  debt_free_date: string;
  projections: PayoffProjection[];
}

export default function DebtPlanner() {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [selectedDebtForPayment, setSelectedDebtForPayment] = useState<Debt | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Debt | null>(null);

  const [totalBalance, setTotalBalance] = useState(0);
  const [totalMinPayment, setTotalMinPayment] = useState(0);

  const [strategy, setStrategy] = useState<'avalanche' | 'snowball'>('avalanche');
  const [extraPayment, setExtraPayment] = useState('0');
  const [payoffPlan, setPayoffPlan] = useState<PayoffPlan | null>(null);
  const [_isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    void loadDebts();
  }, []);

  useEffect(() => {
    if (debts.length > 0) {
      void calculatePayoff();
    } else {
      setPayoffPlan(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debts, strategy, extraPayment]);

  const loadDebts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{
        debts: Debt[];
        total_balance: number;
        total_minimum_payment: number;
      }>('/debts');

      setDebts(response.debts || []);
      setTotalBalance(response.total_balance || 0);
      setTotalMinPayment(response.total_minimum_payment || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load debts');
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePayoff = async () => {
    if (debts.length === 0) return;

    setIsCalculating(true);
    try {
      const response = await api.get<PayoffPlan>(
        `/debts/payoff-plan?strategy=${strategy}&extra_payment=${extraPayment || 0}`
      );
      setPayoffPlan(response);
    } catch {
      // ignore calculation errors
    } finally {
      setIsCalculating(false);
    }
  };

  const openCreate = () => {
    setEditingDebt(null);
    setShowForm(true);
  };

  const openEdit = (debt: Debt) => {
    setEditingDebt(debt);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDebt(null);
  };

  const openRecordPayment = (debt: Debt) => {
    setSelectedDebtForPayment(debt);
    setShowPaymentForm(true);
  };

  const closePayment = () => {
    setShowPaymentForm(false);
    setSelectedDebtForPayment(null);
  };

  const deleteDebt = async (debt: Debt) => {
    try {
      await api.delete(`/debts/${debt.id}`);
      await loadDebts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete debt');
    } finally {
      setConfirmDelete(null);
    }
  };

  const debtFreeDate = payoffPlan?.debt_free_date ? formatShortDate(payoffPlan.debt_free_date) : '—';

  const summary = useMemo(() => {
    return {
      debtCount: debts.length,
    };
  }, [debts.length]);

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Debt planner"
        subtitle="Compare payoff strategies and record payments"
        actions={
          <button onClick={openCreate} className="btn btn-primary shadow-glow">
            <Plus className="w-4 h-4" />
            Add debt
          </button>
        }
      />

      {error && (
        <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {debts.length === 0 ? (
        <Card title="No debts" subtitle="You’re debt free — or add debts to build a payoff plan." className="text-center">
          <div className="mx-auto h-12 w-12 bg-success-bg rounded-full flex items-center justify-center mb-4 border border-success/20">
            <Wallet className="h-6 w-6 text-success" />
          </div>
          <button onClick={openCreate} className="btn btn-primary shadow-glow inline-flex">
            <Plus className="w-4 h-4" />
            Add your first debt
          </button>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SummaryCard
              title="Total debt"
              value={totalBalance}
              valueClassName="text-danger"
              icon={<CreditCard className="w-5 h-5" />}
            />
            <SummaryCard
              title="Minimum monthly"
              value={totalMinPayment}
              icon={<Coins className="w-5 h-5" />}
            />
            <SummaryCard title="Debt-free date" value={debtFreeDate} icon={<Calendar className="w-5 h-5" />} />
          </div>

          <Card
            title="Payoff strategy"
            subtitle="Avalanche saves more interest; snowball optimizes motivation"
            actions={<Badge variant="default">{summary.debtCount} debts</Badge>}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Strategy</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStrategy('avalanche')}
                    className={`p-4 rounded-xl border transition-all duration-200 text-left ${
                      strategy === 'avalanche'
                        ? 'border-primary/30 bg-primary/10'
                        : 'border-border bg-background-subtle/20 hover:bg-background-subtle/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-primary" />
                      <p className="font-semibold text-text-header">Avalanche</p>
                    </div>
                    <p className="mt-2 text-xs text-text-muted">Pay highest interest first (usually saves the most).</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStrategy('snowball')}
                    className={`p-4 rounded-xl border transition-all duration-200 text-left ${
                      strategy === 'snowball'
                        ? 'border-primary/30 bg-primary/10'
                        : 'border-border bg-background-subtle/20 hover:bg-background-subtle/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <p className="font-semibold text-text-header">Snowball</p>
                    </div>
                    <p className="mt-2 text-xs text-text-muted">Pay smallest balance first (quick wins).</p>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Extra monthly payment</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={extraPayment}
                    onChange={(e) => setExtraPayment(e.target.value)}
                    className="input-field pl-7"
                    placeholder="0"
                  />
                </div>
                <p className="mt-2 text-xs text-text-muted">Additional amount to put toward the current priority debt each month.</p>
              </div>
            </div>

            {payoffPlan && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl border border-border bg-background-subtle/20">
                <div>
                  <p className="text-xs text-text-muted">Months</p>
                  <p className="text-xl font-semibold text-text-header">{payoffPlan.total_months}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Total interest</p>
                  <p className="text-xl font-semibold text-danger">{formatCurrency(payoffPlan.total_interest)}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Total paid</p>
                  <p className="text-xl font-semibold text-text-header">{formatCurrency(payoffPlan.total_paid)}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Debt-free by</p>
                  <p className="text-xl font-semibold text-success">{formatShortDate(payoffPlan.debt_free_date)}</p>
                </div>
              </div>
            )}
          </Card>

          <Card title="Your debts" subtitle="Balances update as you record payments">
            <div className="space-y-5">
              {debts.map((debt) => {
                const progress = debt.original_balance > 0 ? ((debt.original_balance - debt.current_balance) / debt.original_balance) * 100 : 0;
                const clampedProgress = Math.min(Math.max(progress, 0), 100);
                const projection = payoffPlan?.projections.find((p) => p.debt_id === debt.id);

                return (
                  <div
                    key={debt.id}
                    className="p-4 rounded-xl border border-transparent hover:border-border hover:bg-background-subtle/30 transition-all duration-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-text-header truncate">{debt.name}</p>
                          <Badge variant="info">{debt.interest_rate}% APR</Badge>
                        </div>
                        <p className="mt-1 text-xs text-text-muted">
                          Min: {formatCurrency(debt.minimum_payment)}/mo{debt.due_day ? ` · Due day: ${debt.due_day}` : ''}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-lg font-semibold text-text-header">{formatCurrency(debt.current_balance)}</p>
                        <p className="text-xs text-text-muted">of {formatCurrency(debt.original_balance)}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-text-muted mb-2">
                        <span>{clampedProgress.toFixed(0)}% paid off</span>
                        {projection && <span>Payoff: {formatShortDate(projection.payoff_date)}</span>}
                      </div>
                      <div className="w-full bg-background-subtle rounded-full h-2 overflow-hidden border border-border">
                        <div className="h-2 rounded-full bg-primary transition-all duration-200" style={{ width: `${clampedProgress}%` }} />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button onClick={() => openRecordPayment(debt)} className="btn btn-primary shadow-glow">
                        <Coins className="w-4 h-4" />
                        Record payment
                      </button>
                      <button onClick={() => openEdit(debt)} className="btn btn-secondary">
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button onClick={() => setConfirmDelete(debt)} className="btn btn-danger">
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}

      <DebtFormModal
        isOpen={showForm}
        onClose={closeForm}
        debt={editingDebt}
        onSubmit={async () => {
          await loadDebts();
          closeForm();
        }}
      />

      <PaymentFormModal
        isOpen={showPaymentForm}
        onClose={closePayment}
        debt={selectedDebtForPayment}
        onSubmit={async () => {
          await loadDebts();
          closePayment();
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && void deleteDebt(confirmDelete)}
        title="Delete debt"
        message={confirmDelete ? `Delete “${confirmDelete.name}”? This will remove it and its payoff projection.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function DebtFormModal({
  isOpen,
  onClose,
  debt,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  debt: Debt | null;
  onSubmit: () => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    name: debt?.name || '',
    original_balance: debt?.original_balance?.toString() || '',
    current_balance: debt?.current_balance?.toString() || '',
    interest_rate: debt?.interest_rate?.toString() || '',
    minimum_payment: debt?.minimum_payment?.toString() || '',
    due_day: debt?.due_day?.toString() || '',
    notes: debt?.notes || '',
  });

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      name: debt?.name || '',
      original_balance: debt?.original_balance?.toString() || '',
      current_balance: debt?.current_balance?.toString() || '',
      interest_rate: debt?.interest_rate?.toString() || '',
      minimum_payment: debt?.minimum_payment?.toString() || '',
      due_day: debt?.due_day?.toString() || '',
      notes: debt?.notes || '',
    });
    setError('');
    setIsSubmitting(false);
  }, [isOpen, debt]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    const currentBalance = parseFloat(formData.current_balance);
    if (Number.isNaN(currentBalance) || currentBalance < 0) {
      setError('Please enter a valid current balance');
      return;
    }

    const interestRate = parseFloat(formData.interest_rate);
    if (Number.isNaN(interestRate) || interestRate < 0) {
      setError('Please enter a valid interest rate');
      return;
    }

    const minimumPayment = parseFloat(formData.minimum_payment);
    if (Number.isNaN(minimumPayment) || minimumPayment < 0) {
      setError('Please enter a valid minimum payment');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        original_balance: parseFloat(formData.original_balance) || currentBalance,
        current_balance: currentBalance,
        interest_rate: interestRate,
        minimum_payment: minimumPayment,
        due_day: formData.due_day ? parseInt(formData.due_day, 10) : null,
        notes: formData.notes.trim() || null,
      };

      if (debt) {
        await api.put(`/debts/${debt.id}`, payload);
      } else {
        await api.post('/debts', payload);
      }

      await onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to save debt');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={debt ? 'Edit debt' : 'Add debt'}
      description="Track APR, minimum payment, and payoff timeline."
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="debt-form" disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Saving…' : debt ? 'Update' : 'Add'}
          </button>
        </div>
      }
    >
      <form id="debt-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Debt name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            className="input-field"
            placeholder="e.g., Credit card, Car loan"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Original balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.original_balance}
                onChange={(e) => setFormData((p) => ({ ...p, original_balance: e.target.value }))}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Current balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.current_balance}
                onChange={(e) => setFormData((p) => ({ ...p, current_balance: e.target.value }))}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Interest rate (APR)</label>
            <div className="relative">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">%</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.interest_rate}
                onChange={(e) => setFormData((p) => ({ ...p, interest_rate: e.target.value }))}
                className="input-field pr-8"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Minimum payment</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.minimum_payment}
                onChange={(e) => setFormData((p) => ({ ...p, minimum_payment: e.target.value }))}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Due day (optional)</label>
            <input
              type="number"
              min="1"
              max="31"
              value={formData.due_day}
              onChange={(e) => setFormData((p) => ({ ...p, due_day: e.target.value }))}
              className="input-field"
              placeholder="e.g., 15"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Notes (optional)</label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              className="input-field"
              placeholder="Any details…"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

function PaymentFormModal({
  isOpen,
  onClose,
  debt,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  debt: Debt | null;
  onSubmit: () => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    amount: debt?.minimum_payment?.toString() || '0',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      amount: debt?.minimum_payment?.toString() || '0',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setError('');
    setIsSubmitting(false);
  }, [isOpen, debt]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!debt) return;

    const amount = parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.post(`/debts/${debt.id}/payments`, {
        amount,
        date: formData.date,
        notes: formData.notes.trim() || null,
      });

      await onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to record payment');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record payment"
      description={debt ? debt.name : undefined}
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="payment-form" disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Recording…' : 'Record'}
          </button>
        </div>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {debt && (
          <div className="p-4 rounded-xl border border-border bg-background-subtle/20">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">Current balance</span>
              <span className="text-sm font-semibold text-text-header">{formatCurrency(debt.current_balance)}</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-text-muted">Minimum payment</span>
              <span className="text-sm text-text-body">{formatCurrency(debt.minimum_payment)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData((p) => ({ ...p, amount: e.target.value }))}
                className="input-field pl-7"
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Notes (optional)</label>
          <input
            type="text"
            value={formData.notes}
            onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
            className="input-field"
            placeholder="e.g., extra payment"
          />
        </div>
      </form>
    </Modal>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}
